import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi, type FirstRoundPassRow, type FirstRoundUserPlan } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { errorMessage, formatDay, formatMoney, isPassPlan } from "./first-round";
import { useConfirm } from "./ui/ConfirmDialog";

const STATE_LABEL: Record<FirstRoundPassRow["state"], string> = {
  unstarted: "Not started",
  active: "Running",
  ended: "Ended",
};

const sourceLabel = (pass: FirstRoundPassRow) => {
  if (pass.source === "coupon") return `Code ${pass.couponCode ?? ""}`.trim();
  if (pass.source === "admin") return "Given by admin";
  if (pass.source === "stripe") return `Bought · ${formatMoney(pass.amountPaid, pass.currency)}`;
  return "—";
};

/**
 * A theResume user's First Round plan: their pass, what's left on it, and the admin actions
 * (grant a pass, add practice interviews, end the pass). Replaces "Change plan" and "Adjust limit",
 * which don't apply to one-off passes.
 */
export function FirstRoundPassPanel({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const key = ["admin-first-round-user", userId];
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => adminApi.getFirstRoundUser(userId),
  });
  const { data: plans = [] } = useQuery({
    queryKey: ["plans", "THERESUME"],
    queryFn: () => adminApi.listPlans("THERESUME"),
  });
  const passPlans = (plans as any[]).filter(isPassPlan);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const done = (text: string) => (next: FirstRoundUserPlan) => {
    qc.setQueryData(key, next);
    setMessage({ ok: true, text });
  };
  const failed = (e: unknown) => setMessage({ ok: false, text: errorMessage(e) });

  if (isLoading)
    return (
      <Card>
        <CardContent className="p-4 text-sm">Loading plan…</CardContent>
      </Card>
    );
  if (error || !data) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-red-600">
          Couldn't load the plan: {errorMessage(error)}
        </CardContent>
      </Card>
    );
  }

  const current = data.current;

  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <CardTitle>First Round plan</CardTitle>
        <CardDescription>
          {current
            ? `${current.planName} pass · ${STATE_LABEL[current.state]}`
            : data.planChosenAt
            ? "Free"
            : "Free · hasn't chosen a plan yet"}
          {` · demo tries used: ${data.demoTriesUsed}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {message && (
          <p
            role="status"
            className={`rounded-sm p-2 text-sm ${
              message.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </p>
        )}

        {current && (
          <div className="grid gap-3 rounded-sm border bg-white p-3 text-sm sm:grid-cols-4">
            <Fact label="From">{sourceLabel(current)}</Fact>
            <Fact label="Got it">{formatDay(current.purchasedAt)}</Fact>
            <Fact label={current.state === "unstarted" ? "Starts" : "Ends"}>
              {current.state === "unstarted"
                ? `At first interview (lapses ${formatDay(current.endsAt)})`
                : formatDay(current.endsAt)}
            </Fact>
            <Fact label="Practice interviews">
              {data.practice
                ? `${data.practice.used} of ${data.practice.limit ?? "unlimited"} used${
                    data.practice.extra ? ` (incl. ${data.practice.extra} added)` : ""
                  }`
                : "—"}
            </Fact>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {current ? (
            <>
              <AddInterviews
                userId={userId}
                onDone={done("Practice interviews added.")}
                onError={failed}
              />
              <EndPass
                userId={userId}
                pass={current}
                onDone={done("Pass ended. The user is on Free now.")}
                onError={failed}
              />
            </>
          ) : (
            <GrantPass
              userId={userId}
              plans={passPlans}
              onDone={done("Pass given.")}
              onError={failed}
            />
          )}
        </div>

        {(data.passes.length > 0 || data.redemptions.length > 0) && (
          <div className="space-y-2 text-sm">
            <h3 className="font-medium text-slate-800">History</h3>
            <table className="w-full text-left">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Pass</th>
                  <th className="py-1 pr-3 font-medium">From</th>
                  <th className="py-1 pr-3 font-medium">Got it</th>
                  <th className="py-1 pr-3 font-medium">Started</th>
                  <th className="py-1 font-medium">State</th>
                </tr>
              </thead>
              <tbody>
                {data.passes.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="py-1 pr-3">{p.planName}</td>
                    <td className="py-1 pr-3">{sourceLabel(p)}</td>
                    <td className="py-1 pr-3">{formatDay(p.purchasedAt)}</td>
                    <td className="py-1 pr-3">{formatDay(p.startedAt)}</td>
                    <td className="py-1">
                      <Badge variant="outline">{STATE_LABEL[p.state]}</Badge>
                      {p.endedReason && (
                        <span className="ml-2 text-xs text-slate-500">{p.endedReason}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.redemptions.length > 0 && (
              <p className="text-slate-600">
                Codes used:{" "}
                {data.redemptions.map((r) => `${r.code} (${formatDay(r.redeemedAt)})`).join(", ")}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GrantPass({
  userId,
  plans,
  onDone,
  onError,
}: {
  userId: string;
  plans: any[];
  onDone: (next: FirstRoundUserPlan) => void;
  onError: (e: unknown) => void;
}) {
  const [confirm, confirmDialog] = useConfirm();
  const [planId, setPlanId] = useState("");
  const [startNow, setStartNow] = useState(true);
  const [reason, setReason] = useState("");
  const grant = useMutation({
    mutationFn: async () => {
      if (!(await confirm({ title: "Give this user a pass at no charge?", confirmLabel: "Give pass" })))
        throw new Error("Action cancelled");
      return adminApi.grantFirstRoundPass(userId, { planId, startNow, reason });
    },
    onSuccess: (next) => {
      setReason("");
      onDone(next);
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "Action cancelled") return;
      onError(e);
    },
  });

  return (
    <div className="space-y-2 rounded-sm border bg-white p-3">
      {confirmDialog}
      <h3 className="text-sm font-medium">Give a pass (no charge)</h3>
      <select
        aria-label="Pass to give"
        className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm"
        value={planId}
        onChange={(e) => setPlanId(e.target.value)}
      >
        <option value="">Choose a pass</option>
        {plans.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName} · {p.accessDays} days
          </option>
        ))}
      </select>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />
        Start the days now (off: they start at the first practice interview)
      </label>
      <Input
        placeholder="Reason (kept in the audit log)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button
        size="sm"
        disabled={!planId || reason.trim().length < 3 || grant.isPending}
        onClick={() => grant.mutate()}
      >
        {grant.isPending ? "Giving..." : "Give pass"}
      </Button>
    </div>
  );
}

function AddInterviews({
  userId,
  onDone,
  onError,
}: {
  userId: string;
  onDone: (next: FirstRoundUserPlan) => void;
  onError: (e: unknown) => void;
}) {
  const [count, setCount] = useState("1");
  const [reason, setReason] = useState("");
  const add = useMutation({
    mutationFn: () => adminApi.addFirstRoundInterviews(userId, { count: Number(count), reason }),
    onSuccess: (next) => {
      setReason("");
      onDone(next);
    },
    onError,
  });
  const valid = /^\d+$/.test(count) && Number(count) >= 1 && Number(count) <= 50;

  return (
    <div className="space-y-2 rounded-sm border bg-white p-3">
      <h3 className="text-sm font-medium">Add practice interviews</h3>
      <p className="text-xs text-slate-500">On top of the plan's number, for this pass only.</p>
      <Input
        type="number"
        min={1}
        max={50}
        aria-label="How many"
        value={count}
        onChange={(e) => setCount(e.target.value)}
      />
      <Input
        placeholder="Reason, e.g. call dropped"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button
        size="sm"
        disabled={!valid || reason.trim().length < 3 || add.isPending}
        onClick={() => add.mutate()}
      >
        {add.isPending ? "Adding..." : "Add"}
      </Button>
    </div>
  );
}

function EndPass({
  userId,
  pass,
  onDone,
  onError,
}: {
  userId: string;
  pass: FirstRoundPassRow;
  onDone: (next: FirstRoundUserPlan) => void;
  onError: (e: unknown) => void;
}) {
  const [confirm, confirmDialog] = useConfirm();
  const [reason, setReason] = useState("");
  const end = useMutation({
    mutationFn: async () => {
      if (
        !(await confirm({
          title: `End this ${pass.planName} pass now?`,
          description: "The user drops to Free. Refund any payment in Stripe separately.",
          confirmLabel: "End pass",
          destructive: true,
        }))
      ) {
        throw new Error("Action cancelled");
      }
      return adminApi.endFirstRoundPass(userId, { passId: pass.id, reason });
    },
    onSuccess: (next) => {
      setReason("");
      onDone(next);
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "Action cancelled") return;
      onError(e);
    },
  });

  return (
    <div className="space-y-2 rounded-sm border border-red-200 bg-white p-3">
      {confirmDialog}
      <h3 className="text-sm font-medium">End pass</h3>
      <p className="text-xs text-slate-500">Super Admin only. Doesn't refund: do that in Stripe.</p>
      <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Button
        size="sm"
        variant="destructive"
        disabled={reason.trim().length < 3 || end.isPending}
        onClick={() => end.mutate()}
      >
        {end.isPending ? "Ending..." : "End pass"}
      </Button>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium text-slate-900">{children}</div>
    </div>
  );
}
